import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Users,
  FileSearch,
  GitMerge,
  Settings,
  Menu,
  LogOut,
  MessageCircle,
  Plus,
  LayoutDashboard,
  Upload,
  Search,
  FolderOpen,
  Building2,
  ShieldCheck,
  UserCog,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { OnboardingTrigger } from '@/modules/core/dossier-company/components/onboarding-trigger';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Navigation par module
const moduleNavigation: Record<string, { title: string; items: NavItem[] }> = {
  itc: {
    title: 'ITC',
    items: [
      { name: 'Candidats', href: '/itc/candidates', icon: Users },
      { name: 'Importer un CV', href: '/itc/candidates/import', icon: Upload },
      { name: 'Nouveau candidat', href: '/itc/candidates/new', icon: Plus },
      { name: 'Recherche IA', href: '/itc/search', icon: FileSearch },
    ],
  },
  tm: {
    title: 'TENDERS MANAGEMENT',
    items: [
      { name: 'Opportunités', href: '/tm/opportunities', icon: FolderOpen },
      // Sprint 5b.1.5 v2 — Opportunités suggérées (sourcing automatique)
      { name: 'Suggérées', href: '/tm/suggested-opportunities', icon: BarChart3 },
      { name: 'Rechercher', href: '/tm/search', icon: Search },
      { name: 'Sociétés', href: '/tm/companies', icon: Building2 },
      // Sprint 5 v3 — Dossier company (référentiel transverse)
      { name: 'Dossier company', href: '/dossier-company', icon: FolderOpen },
    ],
  },
  bridge: {
    title: 'Bridge',
    items: [
      { name: 'Matching', href: '/bridge', icon: GitMerge },
    ],
  },
  admin: {
    title: 'ADMINISTRATION',
    items: [
      { name: 'Entreprises', href: '/admin/companies', icon: Building2 },
      { name: 'Utilisateurs', href: '/admin/users', icon: UserCog },
      { name: 'Statistiques', href: '/admin/analytics', icon: BarChart3 },
    ],
  },
};

// Déterminer le module actuel depuis le path
function getCurrentModule(pathname: string): string | null {
  if (pathname.startsWith('/itc')) return 'itc';
  if (pathname.startsWith('/tm')) return 'tm';
  // Sprint 5 v3 — Dossier company affiché dans la nav TM
  if (pathname.startsWith('/dossier-company')) return 'tm';
  if (pathname.startsWith('/bridge')) return 'bridge';
  if (pathname.startsWith('/admin')) return 'admin';
  return null;
}

export function MainLayout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const currentModule = getCurrentModule(location.pathname);
  const moduleNav = currentModule ? moduleNavigation[currentModule] : null;

  return (
    <div className="min-h-screen bg-surface">
      {/* Sidebar - Dark theme BPA */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-ink transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
      >
        {/* Logo + Logout */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-white/10">
          {sidebarOpen && (
            <Link to="/dashboard" className="font-display text-2xl text-surface tracking-tight">
              BPA
            </Link>
          )}
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              title="Déconnexion"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-surface/70 hover:text-surface hover:bg-white/10"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {/* Dashboard link - toujours visible */}
            <li>
              <Link
                to="/dashboard"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                  location.pathname === '/dashboard'
                    ? 'bg-bpa-accent text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">Dashboard</span>}
              </Link>
            </li>

            {/* Administration - super_admin uniquement */}
            {isSuperAdmin && (
              <li>
                <Link
                  to="/admin/companies"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-bpa-accent text-white'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <ShieldCheck className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && <span className="font-medium">Administration</span>}
                </Link>
              </li>
            )}

            {/* Module navigation - si dans un module */}
            {moduleNav && (
              <>
                {sidebarOpen && (
                  <li className="pt-6 pb-2">
                    <span className="px-3 text-xs font-semibold text-copper uppercase tracking-widest">
                      {moduleNav.title}
                    </span>
                  </li>
                )}
                {moduleNav.items.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                          isActive
                            ? 'bg-bpa-accent text-white'
                            : 'text-gray-300 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        {sidebarOpen && <span className="font-medium">{item.name}</span>}
                      </Link>
                    </li>
                  );
                })}
              </>
            )}
          </ul>
        </nav>

        {/* User */}
        <div className="border-t border-white/10 p-4">
          <div className={`flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
            <div className="h-9 w-9 rounded-full bg-copper/20 flex items-center justify-center text-copper font-semibold text-sm flex-shrink-0">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-surface/50 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <div className={`mt-3 flex ${sidebarOpen ? 'gap-2' : 'flex-col gap-2'}`}>
            <Link to="/contact" className="flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
              >
                <MessageCircle className="h-4 w-4" />
                {sidebarOpen && <span className="ml-2">Contact</span>}
              </Button>
            </Link>
            <Link to="/settings" className="flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
              >
                <Settings className="h-4 w-4" />
                {sidebarOpen && <span className="ml-2">Paramètres</span>}
              </Button>
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`min-h-screen transition-all duration-300 bg-surface ${
          sidebarOpen ? 'ml-64' : 'ml-16'
        }`}
      >
        <div className="px-4 py-6 lg:px-6">
          <Outlet />
        </div>
      </main>

      {/* Sprint 5b.1 v2 — Wizard onboarding auto-déclenché au premier login (tenant_admin) */}
      <OnboardingTrigger />
    </div>
  );
}
